<template>
	<div :class="bemm()">
		<p v-if="title" :class="bemm('title')">{{ title }}</p>
		<ul :class="bemm('list')">
			<li
				v-for="(item, index) in items"
				:key="item.id ?? `${item.label}-${index}`"
				:class="bemm('item')"
				:style="
					item.status === ChecklistStatus.IDLE
						? '--checklist-item-color: color-mix(in srgb, var(--color-foreground), transparent 50%)'
						: `--checklist-item-color: var(--color-${item.status})`
				"
			>
				<transition name="status-pop" mode="out-in">
					<Icon
						:key="`${item.id ?? index}-${item.status}`"
						:name="statusIcons[item.status]"
						:class="bemm('icon')"
					/>
				</transition>
				<span :class="bemm('label')">{{ item.label }}</span>
			</li>
		</ul>
	</div>
</template>

<script lang="ts" setup>
import { useBemm } from 'bemm';
import { Icon } from '../Icon';
import { Icons } from '../Icon';
import {
	ChecklistStatus,
	type ChecklistStatus as ChecklistStatusType,
	type ChecklistProps,
} from './Checklist.model';

withDefaults(defineProps<ChecklistProps>(), {
	title: '',
});

const bemm = useBemm('ar-checklist', {
	return: 'string',
	includeBaseClass: true,
});

const statusIcons: Record<ChecklistStatusType, string> = {
	[ChecklistStatus.IDLE]: Icons.MINUS_SMALL,
	[ChecklistStatus.SUCCESS]: Icons.CHECK,
	[ChecklistStatus.ERROR]: Icons.CROSS_SMALL,
	[ChecklistStatus.WARNING]: Icons.EXCLAMATION,
	[ChecklistStatus.INFO]: Icons.INFO,
};
</script>

<style lang="scss">
.ar-checklist {
	$b: &;
	display: flex;
	flex-direction: column;
	gap: var(--space-xs);

	&__title {
		margin: 0;
		font-size: var(--font-size-s);
		font-weight: var(--font-weight-medium);
		color: color-mix(in srgb, var(--color-foreground), transparent 20%);
	}

	&__list {
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: calc(var(--space-xs) * 1.5);
		list-style: none;
	}

	&__item {
		--checklist-item-color: color-mix(
			in srgb,
			var(--color-foreground),
			transparent 50%
		);
		--checklist-item-icon-bg: color-mix(
			in srgb,
			var(--checklist-item-color),
			transparent 86%
		);
		--checklist-item-icon-border: color-mix(
			in srgb,
			var(--checklist-item-color),
			transparent 80%
		);
		--checklist-item-text-color: color-mix(
			in srgb,
			var(--color-foreground),
			var(--checklist-item-color) 20%
		);
		display: flex;
		align-items: center;
		gap: var(--space-xs);
		padding: calc(var(--space-xs) * 0.5) 0;
		line-height: 1.3;
	}

	&__icon {
		color: var(--checklist-item-color);
		font-size: var(--font-size-xs);
		flex-shrink: 0;
		display: grid;
		place-items: center;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		border: 1px solid var(--checklist-item-icon-border);
		background: var(--checklist-item-icon-bg);
	}

	&__label {
		font-size: var(--font-size-s);
		font-weight: var(--font-weight-medium);
		color: var(--checklist-item-text-color);
	}
}

.status-pop-enter-active,
.status-pop-leave-active {
	transition:
		transform 0.22s ease,
		opacity 0.22s ease;
}

.status-pop-enter-from,
.status-pop-leave-to {
	transform: scale(0.65);
	opacity: 0.35;
}

.status-pop-enter-to,
.status-pop-leave-from {
	transform: scale(1);
	opacity: 1;
}
</style>
